import { Controller } from "@hotwired/stimulus"

// Mission-control shell: Sheets → Legends → Symbol Detection → Symbol Review → Take-off
export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = { defaultStage: { type: String, default: "sheets" } }

  connect() {
    this._lastAutoStage = null
    this._pendingLegendsAdvance = false
    this._onStreamRender = this.onStreamRender.bind(this)
    document.addEventListener("turbo:before-stream-render", this._onStreamRender)
    const fromHash = this.stageFromLocation()
    const def = this.defaultStageValue || "sheets"
    // After the legends job, show() left #stage-legends. If the server now
    // wants Detect (legends complete), do not trap the operator on Legends.
    if (fromHash === "legends" && def === "detect") {
      this.show("detect")
      return
    }
    this.show(fromHash || def)
  }

  disconnect() {
    if (this._onStreamRender) {
      document.removeEventListener("turbo:before-stream-render", this._onStreamRender)
    }
  }

  // Turbo replace of cd_phase_list re-hides rails from server cockpit_mode.
  // Re-apply the viewed tab so Sheets/Legends/Detect/Review/Take-off stay aligned.
  onStreamRender(event) {
    const orig = event.detail?.render
    const resync = () => this.syncCockpitRail(this.currentStage())
    if (typeof orig !== "function") {
      requestAnimationFrame(resync)
      return
    }
    event.detail.render = async (streamElement) => {
      await orig(streamElement)
      resync()
    }
  }

  currentStage() {
    const tab = this.tabTargets.find((t) => t.classList.contains("is-current"))
    return this.normalize(tab?.dataset?.stage || this.defaultStageValue || "sheets")
  }

  showStage(event) {
    const stage = event.currentTarget?.dataset?.stage
    if (!stage) return
    this.show(stage)
  }

  followCta(event) {
    const el = event.currentTarget
    const stage = el?.dataset?.stage
    if (stage) {
      event.preventDefault()
      this.show(stage)
      return
    }
    const href = el?.getAttribute("href") || ""
    event.preventDefault()
    this.show(this.normalize(href.replace(/^#/, "") || "sheets"))
  }

  // Called from ingest-status poll when a background task is active.
  autoStageForTask(taskName, { busy = false, complete = false, legendsComplete = false } = {}) {
    const name = `${taskName || ""}`.toLowerCase()
    if (!busy && !complete) return
    let stage = null
    if (busy) {
      if (name === "ingest") stage = "sheets"
      else if (name === "legend_catalog") stage = "legends"
      else if (name === "analysis" || name.includes("symbol")) stage = "detect"
      else if (name === "markup" || name === "takeoff") stage = "takeoff"
      else if (name === "learning") stage = "review"
    } else if (complete && name === "analysis") {
      // After Symbol Detection finishes → Symbol Review (not Legends).
      stage = "review"
    } else if (complete && name === "legend_catalog") {
      stage = "legends"
    }
    if (!stage || stage === this._lastAutoStage) return
    this._lastAutoStage = stage
    this.show(stage)
  }

  show(stage) {
    const key = this.normalize(stage)
    this.tabTargets.forEach((tab) => {
      const on = tab.dataset.stage === key
      tab.classList.toggle("is-current", on)
      tab.setAttribute("aria-selected", on ? "true" : "false")
    })
    this.panelTargets.forEach((panel) => {
      const on = panel.dataset.stage === key
      panel.hidden = !on
      panel.classList.toggle("is-current", on)
    })
    this.syncCockpitRail(key)
    if (key === "review") {
      document.dispatchEvent(new CustomEvent("cd:panel-opened", { detail: { section: "symbols" } }))
    }
    if (window.history?.replaceState) {
      window.history.replaceState(null, "", `#stage-${key}`)
    }
  }

  // Tab, panel, and live-phase rail share the same stage key.
  syncCockpitRail(stage) {
    const key = this.normalize(stage)
    this.element.querySelectorAll("[data-cockpit-rail]").forEach((el) => {
      const on = el.dataset.cockpitRail === key
      el.hidden = !on
      if (on) {
        const sub = this.element.querySelector("[data-ingest-text='cockpit_subtitle']")
        const title = el.dataset.cockpitSubtitle
        if (sub && title) sub.textContent = title
      }
    })
  }

  stageFromLocation() {
    const hash = (window.location.hash || "").replace(/^#/, "")
    if (!hash) return null
    return this.normalize(hash.replace(/^stage-/, ""))
  }

  normalize(stage) {
    const s = String(stage || "sheets")
    if (s === "sheets" || s === "scope" || s === "identify") return "sheets"
    if (s === "legends" || s === "legend" || s === "catalog") return "legends"
    if (s === "ingest" || s === "detect" || s === "symbols_ingest" || s === "analysis") return "detect"
    if (s === "symbols" || s === "review") return "review"
    if (s === "takeoff" || s === "results" || s === "markup") return "takeoff"
    return s
  }
}
